/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module NavigationModule
 */

import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';

import { BehaviorSubject } from 'rxjs';
import { TransitionService } from '@uirouter/core';
import { IAppState } from 'ajs/js/services/appStates.types';
import { INavState } from '../../../navigation.types';
import { IsActiveStatePipe } from '../../../pipes/is-active-state.pipe';
import './vertical-nav-group.component.less';

/**
 * @desc
 *     Component for a vertical nav group in the vertical nav. Checks for changes to the active
 *     state to determine if the group should be expanded.
 * @author alextsg
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'vertical-nav-group',
    templateUrl: './vertical-nav-group.component.html',
})
export class VerticalNavGroupComponent implements OnInit, OnDestroy {
    @Input()
    public state: INavState;

    /**
     * Indicates if the entire vertical nav has been collapsed. This is used as a workaround for
     * Clarity, since Clarity's vertical nav component looks for a child
     * 'clr-vertical-nav-group-children' component and adds classes to it on collapse. This doesn't
     * work when nested components are used and ChangeDetectionStrategy.OnPush is enabled, so this
     * binding is added as a way to communicate to this component that it needs to be rerendered.
     */
    @Input()
    public collapsed = false;

    /**
     * Observable for the expanded state of the nav group.
     */
    public expanded$ = new BehaviorSubject(false);

    // eslint-disable-next-line @typescript-eslint/ban-types
    private unregisterTransitionSuccess: Function;

    constructor(
        private readonly $transitions: TransitionService,
        private readonly isActiveStatePipe: IsActiveStatePipe,
    ) {}

    /** @override */
    public ngOnInit(): void {
        this.setExpanded();

        this.unregisterTransitionSuccess = this.$transitions.onSuccess(
            {},
            () => this.setExpanded(),
        );
    }

    /** @override */
    public ngOnDestroy(): void {
        // TODO: Fix permissionCheck calling ngOnDestroy on an already destroyed component.
        // AV-207019
        this.unregisterTransitionSuccess?.();
    }

    /**
     * Handle expanded state change of the clrVerticalNavGroup.
     */
    public handleExpandedChange(expanded: boolean): void {
        this.expanded$.next(expanded);
    }

    /**
     * Track by function for ngFor iterator on states.
     */
    public trackByState(index: number, state: IAppState): string {
        return state.name;
    }

    /**
     * Check the active state and push the expanded$ value if it's true. A new value should not be
     * pushed if it's false because groups should not be closed without user interaction. Groups
     * should be opened if one of its child states becomes active.
     */
    private setExpanded(): void {
        const isActive = this.isActiveStatePipe.transform(this.state);

        if (isActive) {
            this.expanded$.next(true);
        }
    }
}
